<script lang="ts">
  import type { ApiResponse } from '@/types';
  import { apiClient } from '@/utils/api';
  import { stats } from '../stores/editor';

  type Summary = {
    views_count: number;
    clicks_count: number;
    ctr: number;
    email_captures_count: number;
  };

  type TimeseriesPoint = { date: string; count: number };
  type TopLink = { id: string; title: string; clicks_count: number };
  type GroupedRow = { count: number; device_type?: string; referrer_url?: string; country_code?: string };
  type SummaryResponse = {
    summary?: Summary;
    data?: {
      summary?: Summary;
    };
    error?: string;
  };
  type CollectionResponse<T> = {
    [key: string]: unknown;
    data?: Record<string, T[]> | CollectionResponse<T>;
    error?: string;
  };

  let period = $state('30d');
  let loading = $state(false);
  let error = $state('');

  let summary = $state<Summary>({
    views_count: 0,
    clicks_count: 0,
    ctr: 0,
    email_captures_count: 0,
  });
  let timeseries = $state<TimeseriesPoint[]>([]);
  let topLinks = $state<TopLink[]>([]);
  let devices = $state<GroupedRow[]>([]);
  let referrers = $state<GroupedRow[]>([]);
  let locations = $state<GroupedRow[]>([]);

  let requestVersion = 0;

  const maxTimeseriesCount = $derived(
    Math.max(1, ...timeseries.map((point) => point.count))
  );

  const latestPeriodLabel = $derived(
    period === '7d' ? '최근 7일' : period === '90d' ? '최근 90일' : period === 'all' ? '전체' : '최근 30일'
  );

  function rowLabel(row: GroupedRow) {
    return row.device_type || row.referrer_url || row.country_code || 'Unknown';
  }

  function formatDate(value: string) {
    return new Date(value).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
  }

  function responseCollection<T>(response: ApiResponse<CollectionResponse<T>>, key: string) {
    const topLevel = (response as unknown as CollectionResponse<T>)[key];
    const nested = response.data && typeof response.data === 'object'
      ? (response.data as CollectionResponse<T>)[key]
      : null;
    return (topLevel || nested) as T[] | null;
  }

  function responseSummary(response: ApiResponse<SummaryResponse>) {
    const topLevel = (response as unknown as SummaryResponse).summary;
    return topLevel || response.data?.summary || null;
  }

  async function fetchCollection<T>(endpoint: string, key: string, selectedPeriod: string) {
    const response = await apiClient.get<CollectionResponse<T>>(
      endpoint,
      { params: { period: selectedPeriod }, skipCaseConversion: true }
    );

    if (!response.success) return null;
    return responseCollection(response, key);
  }

  async function loadAnalytics(selectedPeriod: string) {
    const currentVersion = ++requestVersion;
    loading = true;
    error = '';

    try {
      const summaryResponse = await apiClient.get<SummaryResponse>(
        '/api/v1/profile_analytics/summary',
        { params: { period: selectedPeriod }, skipCaseConversion: true }
      );

      if (currentVersion !== requestVersion) return;

      if (!summaryResponse.success) {
        error = summaryResponse.error || '분석 데이터를 불러오지 못했습니다.';
        return;
      }

      const nextSummary = responseSummary(summaryResponse);
      if (nextSummary) {
        summary = nextSummary;
        stats.set({
          total_views: nextSummary.views_count,
          total_clicks: nextSummary.clicks_count,
        });
      }

      const [nextTimeseries, nextTopLinks, nextDevices, nextReferrers, nextLocations] = await Promise.all([
        fetchCollection<TimeseriesPoint>('/api/v1/profile_analytics/timeseries', 'timeseries', selectedPeriod),
        fetchCollection<TopLink>('/api/v1/profile_analytics/top_links', 'top_links', selectedPeriod),
        fetchCollection<GroupedRow>('/api/v1/profile_analytics/devices', 'device_types', selectedPeriod),
        fetchCollection<GroupedRow>('/api/v1/profile_analytics/referrers', 'referrer_urls', selectedPeriod),
        fetchCollection<GroupedRow>('/api/v1/profile_analytics/locations', 'country_codes', selectedPeriod),
      ]);

      if (currentVersion !== requestVersion) return;

      timeseries = nextTimeseries || [];
      topLinks = nextTopLinks || [];
      devices = nextDevices || [];
      referrers = nextReferrers || [];
      locations = nextLocations || [];
    } finally {
      if (currentVersion === requestVersion) {
        loading = false;
      }
    }
  }

  $effect(() => {
    loadAnalytics(period);
  });
</script>

<section class="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
  <div class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
    <div>
      <h2 class="text-lg font-semibold text-gray-950">Analytics</h2>
      <p class="mt-1 text-sm leading-6 text-gray-600">
        요약 지표, 시계열, 상위 링크, 유입 소스/디바이스/국가 분포를 API 기준으로 확인합니다.
      </p>
    </div>

    <div class="flex items-center gap-2">
      {#each ['7d', '30d', '90d', 'all'] as option}
        <button
          type="button"
          class={`rounded-full px-4 py-2 text-sm font-medium transition ${
            period === option
              ? 'bg-gray-950 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          onclick={() => (period = option)}
        >
          {option === 'all' ? '전체' : option}
        </button>
      {/each}
    </div>
  </div>

  {#if error}
    <div class="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
      {error}
    </div>
  {/if}

  <div class="mt-6 grid gap-4 md:grid-cols-4">
    <div class="rounded-2xl border border-gray-200 bg-gray-50 p-4">
      <div class="text-xs uppercase tracking-wide text-gray-500">Views</div>
      <div class="mt-2 text-3xl font-semibold text-gray-950">{summary.views_count}</div>
    </div>
    <div class="rounded-2xl border border-gray-200 bg-gray-50 p-4">
      <div class="text-xs uppercase tracking-wide text-gray-500">Clicks</div>
      <div class="mt-2 text-3xl font-semibold text-gray-950">{summary.clicks_count}</div>
    </div>
    <div class="rounded-2xl border border-gray-200 bg-gray-50 p-4">
      <div class="text-xs uppercase tracking-wide text-gray-500">CTR</div>
      <div class="mt-2 text-3xl font-semibold text-gray-950">{summary.ctr.toFixed(1)}%</div>
    </div>
    <div class="rounded-2xl border border-gray-200 bg-gray-50 p-4">
      <div class="text-xs uppercase tracking-wide text-gray-500">Email Captures</div>
      <div class="mt-2 text-3xl font-semibold text-gray-950">{summary.email_captures_count}</div>
    </div>
  </div>

  <div class="mt-6 grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
    <div class="space-y-6">
      <div class="rounded-3xl border border-gray-200 p-5">
        <div class="flex items-center justify-between gap-3">
          <div class="text-sm font-semibold text-gray-900">Views Timeline</div>
          <div class="text-xs text-gray-500">{latestPeriodLabel}</div>
        </div>

        <div class="mt-5 grid h-52 grid-cols-[repeat(auto-fit,minmax(10px,1fr))] items-end gap-2">
          {#each timeseries as point}
            <div class="flex flex-col items-center gap-2">
              <div
                class="w-full rounded-t-full bg-gradient-to-t from-sky-500 to-cyan-300"
                style={`height:${Math.max(8, (point.count / maxTimeseriesCount) * 160)}px;`}
                title={`${formatDate(point.date)} · ${point.count}`}
              ></div>
              <div class="text-[10px] text-gray-400">{formatDate(point.date)}</div>
            </div>
          {:else}
            <div class="col-span-full flex items-center justify-center rounded-2xl bg-gray-50 text-sm text-gray-500">
              {loading ? '불러오는 중...' : '시계열 데이터가 없습니다.'}
            </div>
          {/each}
        </div>
      </div>

      <div class="rounded-3xl border border-gray-200 p-5">
        <div class="text-sm font-semibold text-gray-900">Top Links</div>
        <div class="mt-4 space-y-2">
          {#each topLinks as link, index}
            <div class="flex items-center justify-between rounded-xl bg-gray-50 px-4 py-3 text-sm">
              <div class="flex items-center gap-3">
                <span class="flex h-7 w-7 items-center justify-center rounded-full bg-white text-xs font-semibold text-gray-600">{index + 1}</span>
                <span class="font-medium text-gray-900">{link.title}</span>
              </div>
              <span class="text-gray-500">{link.clicks_count} clicks</span>
            </div>
          {:else}
            <div class="text-sm text-gray-500">{loading ? '불러오는 중...' : '클릭 데이터가 없습니다.'}</div>
          {/each}
        </div>
      </div>
    </div>

    <aside class="space-y-4">
      <div class="rounded-3xl border border-gray-200 bg-gray-50 p-5">
        <div class="text-sm font-semibold text-gray-900">Devices</div>
        <div class="mt-3 space-y-2">
          {#each devices as row}
            <div class="flex items-center justify-between rounded-xl bg-white px-4 py-3 text-sm">
              <span class="font-medium text-gray-800">{rowLabel(row)}</span>
              <span class="text-gray-500">{row.count}</span>
            </div>
          {:else}
            <div class="text-sm text-gray-500">데이터가 없습니다.</div>
          {/each}
        </div>
      </div>

      <div class="rounded-3xl border border-gray-200 bg-gray-50 p-5">
        <div class="text-sm font-semibold text-gray-900">Referrers</div>
        <div class="mt-3 space-y-2">
          {#each referrers as row}
            <div class="flex items-center justify-between gap-3 rounded-xl bg-white px-4 py-3 text-sm">
              <span class="truncate font-medium text-gray-800">{rowLabel(row)}</span>
              <span class="shrink-0 text-gray-500">{row.count}</span>
            </div>
          {:else}
            <div class="text-sm text-gray-500">데이터가 없습니다.</div>
          {/each}
        </div>
      </div>

      <div class="rounded-3xl border border-gray-200 bg-gray-50 p-5">
        <div class="text-sm font-semibold text-gray-900">Locations</div>
        <div class="mt-3 space-y-2">
          {#each locations as row}
            <div class="flex items-center justify-between rounded-xl bg-white px-4 py-3 text-sm">
              <span class="font-medium text-gray-800">{rowLabel(row)}</span>
              <span class="text-gray-500">{row.count}</span>
            </div>
          {:else}
            <div class="text-sm text-gray-500">데이터가 없습니다.</div>
          {/each}
        </div>
      </div>
    </aside>
  </div>
</section>
